<template>
  <main id="main">
    <header class="header_page fadeIn mb-4">
        <p id="title_header_page" class="mr-3 bold">{{ t('request_registration') }}</p>
        <img src="@/assets/imgs/back.svg" class="icon-size-3" alt="" @click="$router.go(-1)">
    </header>

    <div v-if="loading" class="text-center mt-5">
        <p>{{ t('loading') }}</p>
    </div>

    <div v-else class="w-100 p-3 d-flex flex-column gap-4">
        
        <section class="w-100">
            <p class="font_normal bold mb-3 text-center text_color_theme">
                {{ t('what_lesson') }}
            </p>
            <div class="bg_white rounded-5 shadow p-3 d-flex flex-wrap gap-2 justify-content-center">
                <div 
                    v-for="(lesson, index) in lessonOptions" 
                    :key="index"
                    class="chip px-3 py-2 rounded-pill cursor-pointer d-flex align-items-center gap-2"
                    :class="selectedLessons.includes(lesson) ? 'bg_light_theme text_white' : 'bg_gray_light text_color_gray'"
                    @click="toggleSelection('lesson', lesson)"
                >
                    <span>{{ lesson }}</span>
                    <span v-if="selectedLessons.includes(lesson)">✕</span>
                </div>
            </div>
        </section>

        <section class="w-100">
            <p class="font_normal bold mb-3 text-center text_color_theme">
                {{ t('what_grade') }}
            </p>
            <div class="bg_white rounded-5 shadow p-3 d-flex flex-wrap gap-2 justify-content-center">
                 <div 
                    v-for="(grade, index) in gradeOptions" 
                    :key="index"
                    class="chip px-3 py-2 rounded-pill cursor-pointer d-flex align-items-center gap-2"
                    :class="selectedGrades.includes(grade) ? 'bg_light_theme text_white' : 'bg_gray_light text_color_gray'"
                    @click="toggleSelection('grade', grade)"
                >
                    <span>{{ grade }}</span>
                    <span v-if="selectedGrades.includes(grade)">✕</span>
                </div>
            </div>
        </section>

        <section class="w-100">
            <p class="font_normal bold mb-3 text-center text_color_theme">
                {{ t('strength_point') }}
            </p>
            <textarea 
                v-model="ability" 
                class="w-100 rounded-5 shadow p-3 border_gray" 
                rows="6" 
                :placeholder="t('optional')"
            ></textarea>
        </section>

        <button class="button mt-2" @click="submitRequest" :disabled="submitting">
            {{ submitting ? t('loading') : t('submit_post') }}
        </button>
    </div>
  </main>
</template>

<script>
import apiClient, { createFormData } from '@/services/api'
import { Decryption } from '@/utils/crypto'

export default {
  name: 'TeacherRequestSubject',
  data() {
    return {
        lessonOptions: [],
        gradeOptions: [],
        selectedLessons: [],
        selectedGrades: [],
        ability: '',
        loading: false,
        submitting: false
    }
  },
  mounted() {
    this.fetchOptions();
  },
  methods: {
    async fetchOptions() {
        this.loading = true;
        try {
            // Fetch Lessons
            const lessonsFormData = createFormData({ action: 'lessons' });
            const lessonsRes = await apiClient.post('/public_info', lessonsFormData);
            
            if (lessonsRes.data && lessonsRes.data.data) {
                let lessonsData = lessonsRes.data.data;
                
                // Decrypt if necessary
                if (typeof lessonsData === 'string') {
                    lessonsData = Decryption(lessonsData);
                }
                
                if (Array.isArray(lessonsData)) {
                    this.lessonOptions = lessonsData.map(l => l.title);
                }
            }

            // Fetch Grades
            const gradesFormData = createFormData({ action: 'grade' });
            const gradesRes = await apiClient.post('/public_info', gradesFormData);
            
            if (gradesRes.data) {
                let gradesData = gradesRes.data;
                
                // Handle different response structures
                if (gradesData.data) {
                    gradesData = gradesData.data;
                }
                
                // Decrypt if necessary
                if (typeof gradesData === 'string') {
                    gradesData = Decryption(gradesData);
                }
                
                if (Array.isArray(gradesData)) {
                    this.gradeOptions = gradesData.map(g => g.title);
                }
            }

        } catch (error) {
            console.error(error);
            this.notify(this.t('connection_error'), 'error');
        } finally {
            this.loading = false;
        }
    },
    toggleSelection(type, item) {
        if (type === 'lesson') {
            if (this.selectedLessons.includes(item)) {
                this.selectedLessons = this.selectedLessons.filter(i => i !== item);
            } else {
                this.selectedLessons.push(item);
            }
        } else if (type === 'grade') {
            if (this.selectedGrades.includes(item)) {
                this.selectedGrades = this.selectedGrades.filter(i => i !== item);
            } else {
                this.selectedGrades.push(item);
            }
        }
    },
    async submitRequest() {
        if (this.selectedLessons.length === 0) {
            this.notify(this.t('fill_required'), 'error');
            return;
        }
        if (this.selectedGrades.length === 0) {
            this.notify(this.t('fill_required'), 'error');
            return;
        }

        this.submitting = true;
        try {
            const userId = localStorage.getItem('user_id');
            const formData = createFormData({
                teacher_id: userId,
                lessons: this.selectedLessons.join('، '),
                grades: this.selectedGrades.join('، '),
                ability: this.ability
            });

            const response = await apiClient.post('/teacher/add_teaching_request', formData);

            if (response.data.status === 'ok') {
                this.notify(response.data.message || this.t('successful_submit'), 'success');
                this.$router.go(-1);
            } else {
                this.notify(response.data.message || this.t('error_submit'), 'error');
            }
        } catch (error) {
            console.error(error);
            this.notify(this.t('connection_error'), 'error');
        } finally {
            this.submitting = false;
        }
    }
  }
}
</script>

<style scoped>
.chip {
    transition: all 0.3s ease;
    border: 1px solid transparent;
}
.bg_gray_light {
    background-color: #f0f0f0;
}
.text_color_theme {
    color: #4A00E0;
}
.bg_light_theme {
    background-color: #4A00E0;
}
.text_white {
    color: white;
}
.cursor-pointer {
    cursor: pointer;
}
.border_gray {
    border: 1px solid #e0e0e0;
    outline: none;
}
.border_gray:focus {
    border-color: #4A00E0;
}
</style>